import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { resetPassword } from '@/services/authService';

type Status = 'idle' | 'sending' | 'sent' | 'error';

const RESPONSE_MESSAGE_KEY_MAP: Record<string, string> = {
  'Reset link is invalid or has expired.': 'resetPassword.invalidToken',
};

const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [messageKey, setMessageKey] = useState<string | null>(null);
  const [messageText, setMessageText] = useState<string>('');
  const message = messageKey ? t(messageKey) : messageText;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!token) {
      setStatus('error');
      setMessageKey('resetPassword.missingToken');
      setMessageText('');
      return;
    }

    if (password !== confirmPassword) {
      setStatus('error');
      setMessageKey('resetPassword.passwordMismatch');
      setMessageText('');
      return;
    }

    if (status === 'sending') {
      return;
    }

    setStatus('sending');
    setMessageKey(null);
    setMessageText('');
    try {
      const response = await resetPassword(token, password);
      setStatus('sent');
      if (response.message) {
        setMessageKey(null);
        setMessageText(response.message);
      } else {
        setMessageKey('resetPassword.success');
      }
    } catch (error: any) {
      setStatus('error');
      const responseMessage = error.response?.data?.message;
      if (responseMessage) {
        const key = RESPONSE_MESSAGE_KEY_MAP[responseMessage];
        if (key) {
          setMessageKey(key);
          setMessageText('');
        } else {
          setMessageKey(null);
          setMessageText(responseMessage);
        }
      } else {
        setMessageKey('resetPassword.invalidToken');
      }
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-16 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      <div className="w-full max-w-xl rounded-2xl border border-slate-800 bg-slate-900/70 shadow-2xl p-8">
        <h1 className="text-3xl font-semibold mb-4">{t('resetPassword.title')}</h1>
        <p className="text-slate-200 mb-6">{t('resetPassword.description')}</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder={t('resetPassword.passwordPlaceholder')}
            type="password"
            required
          />
          <Input
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            placeholder={t('resetPassword.confirmPasswordPlaceholder')}
            type="password"
            required
          />
          <Button type="submit" disabled={status === 'sending'}>
            {status === 'sending' ? t('resetPassword.sending') : t('resetPassword.submitButton')}
          </Button>
        </form>

        {message && (
          <p className={`mt-4 text-sm ${status === 'error' ? 'text-red-300' : 'text-green-300'}`}>
            {message}
          </p>
        )}

        <div className="mt-6">
          <Link to="/studio" className="text-sm text-indigo-300 hover:text-indigo-200">
            {t('resetPassword.backToLogin')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
